import { DEFAULT_METRIC, CURRENT_PRODUCT_MODE } from '@utils/constants';
import update from 'immutability-helper';
import * as types from './types';
import { getMetricFilterId } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import { defaultDimensionGroupBy } from '@utils/odp/utils';
import { get } from 'lodash';

export const DEFAULT_DIMENSIONS = {
  EI: ['OBJECT_NAME', 'm3.dv.n', 'LIVE_VIDEO'],
  AI: ['OBJECT_NAME', 'c3.ad.position', 'c3.ad.technology'],
  APP: ['platform', 'event_name'],
  ODP: [defaultDimensionGroupBy],
  ECO_SERVER: [defaultDimensionGroupBy],
};

export const DIMENSION_LIMIT = { min: 1, max: 8 };

export enum ViewContexts {
  DEFAULT = '',
  RT_TRENDS = 'modify-real-time-trends',
  RT_FILTERS = 'modify-real-time-filters',
}

export enum Tabs {
  TopTrends,
  ViewerSessions,
  TopImpactedViewers,
  AppUserSessions,
  AppAnalyseNetworkRequest,
}

export enum COMPARE_TYPES {
  dashboard = 'dashboard',
  trends = 'trends',
}

export const defaultCompareState = {
  filters: [],
  activeFilterIndex: null,
  modifyCompareId: null,
  type: COMPARE_TYPES.dashboard,
};

const initialState = {
  metricFilter: [],
  compareFilters: [],
  dimensions: DEFAULT_DIMENSIONS[CURRENT_PRODUCT_MODE],
  dimensionsToNA: [],
  // filter bar
  rules: [],
  // compare bar
  compare: defaultCompareState,
  // context for table actions
  viewContext: '',
  // dimension / viewers / impacted viewers
  dimensionPanelActiveTab: Tabs.TopTrends,

  // dimensions state
  globalOrderBy: DEFAULT_METRIC,
  dimensionMetrics: [DEFAULT_METRIC],
  dimensionMetricsToNA: [],
};

const switchMap = {};

switchMap[types.INIT] = (state) =>
  update(state, {
    $set: initialState,
  });

switchMap[types.SET_SHARED_STATE] = (state, action) => {
  // for backward compatibility with saved/shared dashboards
  const compareFilters = get(action, ['base', 'compareFilters'], []);
  const formattedCompareFilters = compareFilters.map((f) => {
    const { meta = {} } = f;
    return {
      ...f,
      meta,
    };
  });

  return update(state, {
    $set: {
      ...initialState,
      ...action.base,
      compareFilters: formattedCompareFilters,
    },
  });
};

switchMap[types.SET_METRIC_FILTER] = (state, action) =>
  update(state, {
    metricFilter: {
      $set: action.metricFilter,
    },
  });

switchMap[types.ADD_METRIC_FILTER] = (state, action) => {
  const { metricFilter } = action;

  const ruleIdx = state.metricFilter.findIndex(
    (f) => getMetricFilterId(f) === getMetricFilterId(metricFilter),
  );

  if (ruleIdx > -1) {
    return update(state, {
      metricFilter: {
        $splice: [[ruleIdx, 1, metricFilter]],
      },
    });
  }

  return update(state, {
    metricFilter: {
      $push: [metricFilter],
    },
  });
};

switchMap[types.REMOVE_METRIC_FILTER] = (state, action) => {
  const updatedFilter = state.metricFilter.filter(
    (f) => getMetricFilterId(f) !== getMetricFilterId(action.metricFilter),
  );
  return update(state, {
    metricFilter: {
      $set: updatedFilter,
    },
  });
};

switchMap[types.CLEAR_METRIC_FILTER] = (state) =>
  update(state, {
    metricFilter: {
      $set: [],
    },
  });

switchMap[types.SET_DIMENSIONS] = (state, action) =>
  update(state, {
    dimensions: {
      $set: action.dimensions,
    },
  });

switchMap[types.SET_SELECTED_DIMENSION] = (state, action) =>
  update(state, {
    dimensions: {
      [action.dimensionIndex]: {
        $set: action.dimension,
      },
    },
  });

switchMap[types.SET_DIMENIONS_TO_NA] = (state, action) =>
  update(state, {
    dimensionsToNA: {
      $set: action.dimensions,
    },
  });

switchMap[types.SWAP_SELECTED_DIMENSION] = (state, action) =>
  update(state, {
    dimensions: {
      [action.currentIndex]: {
        $set: action.swapDimension,
      },
      [action.swapIndex]: {
        $set: action.dimension,
      },
    },
  });

switchMap[types.ADD_DIMENSION] = (state, action) =>
  update(state, {
    dimensions: {
      $push: [action.dimension],
    },
  });

switchMap[types.REMOVE_DIMENSION] = (state, action) => {
  const idx = state.dimensions.findIndex((d) => d === action.dimension);
  if (idx > -1) {
    return update(state, {
      dimensions: {
        $splice: [[idx, 1]],
      },
    });
  }
  return state;
};

switchMap[types.SET_RULES] = (state, action) =>
  update(state, {
    rules: {
      $set: action.rules,
    },
  });

switchMap[types.RESET_FILTER] = (state, action) =>
  update(state, {
    rules: {
      $set: [],
    },
  });

switchMap[types.ADD_COMPARE_FILTER] = (state, action) =>
  update(state, {
    compare: {
      filters: {
        $push: [action.filter],
      },
    },
  });

switchMap[types.REMOVE_COMPARE_FILTER] = (state, action) => {
  return update(state, {
    compare: {
      filters: {
        $splice: [[action.idx, 1]],
      },
    },
  });
};

switchMap[types.SET_COMPARE_FILTERS] = (state, action) =>
  update(state, {
    compare: {
      filters: {
        $set: action.filters,
      },
    },
  });

switchMap[types.SET_ACTIVE_FILTER] = (state, action) =>
  update(state, {
    compare: {
      activeFilterIndex: {
        $set: action.activeFilterIndex,
      },
    },
  });

switchMap[types.UPDATE_ACTIVE_FILTER] = (state, action) =>
  update(state, {
    compare: {
      filters: {
        $splice: [[state.compare.activeFilterIndex, 1, action.filter]],
      },
    },
  });

switchMap[types.RESET_COMPARE] = (state) =>
  update(state, {
    compare: {
      $set: defaultCompareState,
    },
    rules: {
      $set: [],
    },
  });

switchMap[types.MODIFY_COMPARE_INIT] = (state, action) =>
  update(state, {
    compare: {
      $set: action.config,
    },
  });

switchMap[types.SET_VIEW_CONTEXT] = (state, action) =>
  update(state, {
    viewContext: {
      $set: action.context,
    },
  });

switchMap[types.SET_DIMENSION_PANEL_TAB] = (state, action) =>
  update(state, {
    dimensionPanelActiveTab: {
      $set: action.tab,
    },
  });

switchMap[types.SET_GLOBAL_ORDER_BY] = (state, action) =>
  update(state, {
    globalOrderBy: {
      $set: action.metric,
    },
  });

switchMap[types.SET_DIMENSION_METRICS] = (state, action) =>
  update(state, {
    dimensionMetrics: {
      $set: action.metrics,
    },
  });

switchMap[types.SET_DIMENIONS_METRICS_TO_NA] = (state, action) =>
  update(state, {
    dimensionsMetricsToNA: {
      $set: action.metrics,
    },
  });

switchMap[types.APPLY_COMPARE_FILTERS_ON_TRENDS] = (state, action) =>
  update(state, {
    compareFilters: {
      $set: action.filters,
    },
  });

switchMap[types.CLEAR_COMPARE_FILTERS_ON_TRENDS] = (state) =>
  update(state, {
    compareFilters: {
      $set: [],
    },
  });

switchMap[types.REMOVE_COMPARE_FILTERS_ON_TRENDS] = (state, action) => {
  return update(state, {
    compareFilters: {
      $splice: [[action.idx, 1]],
    },
  });
};

switchMap[types.ADD_COMPARE_FILTER_ON_TRENDS] = (state, action) => {
  return update(state, {
    compareFilters: {
      $push: [action.filter],
    },
  });
};

switchMap[types.HIGHLIGHT_COMPARE_FILTER] = (state, action) => {
  return update(state, {
    compareFilters: {
      [action.idx]: {
        meta: {
          $merge: {
            highlight: action.toggleState,
          },
        },
      },
    },
  });
};

export default (state = initialState, action) =>
  switchMap[action.type] ? switchMap[action.type](state, action) : state;
